/**
 * Komponent renderujący skondensowany pasek górny dla WiseTable.
 * Dane z kontekstu WiseTable (useWiseTable).
 */

import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Filter, FilterCircle, Gear } from 'react-bootstrap-icons';
import { PageTitle } from 'components/controls';
import WiseTableSearch from 'components/wise/WiseTable/components/WiseTableSearch';
import AdvancedFiltersDialog from 'components/wise/WiseTable/components/AdvancedFiltersDialog';
import FiltersActionPopper from 'components/wise/WiseTable/components/FiltersActionPopper';
import WiseTableNoContextAction from 'components/wise/WiseTable/components/WiseTableNoContextAction';
import WiseTableOpenAction from 'components/wise/WiseTable/components/WiseTableOpenAction';

import { useWiseTable } from 'components/wise/WiseTable/context';

import styles from 'theme/components/wise/WiseTable/components/CondensedTopBar/CondensedTopBar.module.scss';

const CondensedTopBar: FC = () => {
  const { t } = useTranslation();
  const [filtersActionPopperAnchor, setFiltersActionPopperAnchor] =
    useState<HTMLElement | null>(null);

  const {
    config,
    queryParams,
    updateParameter,
    recordCount,
    noContextActions,
    openActions,
    refetchTableData,
    advancedFilters
  } = useWiseTable();

  const {
    label,
    parameters: parametersConfig,
    showPlainTable,
    withAdvancedFilters,
    columns,
    componentParams,
    requestParams
  } = config;

  const {
    onOpenAdvancedFiltersDialog,
    onCloseAdvancedFiltersDialog,
    advancedFiltersDialogOpen,
    setTabIndex,
    handleOpenSaveSettingsNameDialog,
    showColumnFilterIcons,
    onToggleColumnFilterIcons
  } = advancedFilters;

  const searchParameter = useMemo(
    () => parametersConfig.find((param) => param.type === 'search'),
    [parametersConfig]
  );

  const recordCountPart = recordCount ? ` (${recordCount})` : '';
  const titleWithCount = `${label}${recordCountPart}`;

  return (
    <div className={classNames(styles.condensedTopBar, { [styles.plainTable]: showPlainTable })}>
      {!showPlainTable ? (
        <div className={styles.titleWrapper}>
          <PageTitle title={titleWithCount} className={styles.pageTitle} />
          {openActions.length > 0 ? (
            <div className={styles.openActionsWrapper}>
              {openActions.map((action, i) => (
                <WiseTableOpenAction key={i} action={action} requestParams={requestParams} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={styles.actionsWrapper}>
        {searchParameter && (
          <div className={styles.searchWrapper}>
            {withAdvancedFilters && columns.length > 0 && (
              <>
                <button
                  type="button"
                  className={styles.filterIconButton}
                  onClick={(e) => setFiltersActionPopperAnchor(e.currentTarget)}
                  aria-label={t('Akcje filtrów')}
                  aria-haspopup="true"
                  aria-expanded={Boolean(filtersActionPopperAnchor)}>
                  <Gear className={styles.filterIcon} size={20} />
                </button>
                <FiltersActionPopper
                  open={Boolean(filtersActionPopperAnchor)}
                  anchorEl={filtersActionPopperAnchor}
                  onClose={() => setFiltersActionPopperAnchor(null)}
                  onOpenColumnVisibility={() => {
                    setTabIndex(1);
                    onOpenAdvancedFiltersDialog?.();
                  }}
                  onOpenAdvancedFilters={() => {
                    setTabIndex(0);
                    onOpenAdvancedFiltersDialog?.();
                  }}
                  onOpenSavedSettings={() => {
                    setTabIndex(2);
                    onOpenAdvancedFiltersDialog?.();
                  }}
                  onSaveCurrentLayout={() => {
                    setTabIndex(2);
                    onOpenAdvancedFiltersDialog?.();
                    handleOpenSaveSettingsNameDialog?.();
                  }}
                />
                <button
                  type="button"
                  className={styles.filterIconButton}
                  onClick={onToggleColumnFilterIcons}
                  aria-label={t('Filtry kolumn')}
                  title={t('Filtry kolumn')}
                  aria-pressed={showColumnFilterIcons}>
                  {showColumnFilterIcons ? (
                    <FilterCircle className={styles.filterIconActive} size={20} />
                  ) : (
                    <Filter className={styles.filterIcon} size={20} />
                  )}
                </button>
              </>
            )}
            <WiseTableSearch
              placeholder={searchParameter.label}
              onChange={(value) =>
                updateParameter(searchParameter.field_symbol || '', value)
              }
              value={queryParams[searchParameter.field_symbol || ''] || ''}
            />
          </div>
        )}

        {noContextActions.length > 0 && (
          <div className={styles.actionsGroup}>
            {noContextActions.map((action, i) => (
              <WiseTableNoContextAction
                key={i}
                action={action}
                refetch={refetchTableData}
                componentParams={componentParams}
                index={i}
              />
            ))}
          </div>
        )}
      </div>

      {withAdvancedFilters && columns.length > 0 && (
        <AdvancedFiltersDialog
          open={advancedFiltersDialogOpen ?? false}
          onClose={onCloseAdvancedFiltersDialog ?? (() => null)}
          columns={columns}
          {...advancedFilters}
        />
      )}
    </div>
  );
};

export default CondensedTopBar;
